import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { ApiError } from '../../lib/api';
import {
  AUTOSAVE_DEBOUNCE_MS,
  SAVE_STATUS_LABEL,
  computeSaveStatus,
  shouldAutosave,
} from '../../lib/broadcastAutosave';
import { validateSurveyFlow } from '../../lib/surveyBranching';
import { normalizeSurveySteps } from '../../lib/surveySteps';
import type { IntroScreen, OutroScreen, SurveyDraftInput, SurveyLinkMode, SurveyStep } from '../../types/survey';
import { createSurvey, emptySurveyDraft, getSurvey, sendSurvey, surveyToDraft, updateSurvey } from './api';
import { IntroEditor, OutroEditor } from './ScreenEditors';
import { StepList } from './StepList';
import { StepEditorModal } from './QuestionEditors';
import { AudienceTab } from './AudienceTab';
import { SendTab } from './SendTab';

// Phase 6B compose page — /surveys/new + /surveys/:id/edit. B1 scaffold: the
// 3-tab shell (Content / Audience / Send), the survey-title field, and the
// create-on-first-save autosave loop (reusing the broadcast autosave helpers).
// The tab bodies are filled by later sub-phases: intro/outro editors (B2), the
// reorderable step list (B3), per-type question editors (B4), branching (B5),
// audience (B6), send (B7). The Send button is a stub until Phase C.

type Tab = 'content' | 'audience' | 'send';

const TABS: { id: Tab; label: string }[] = [
  { id: 'content', label: 'Content' },
  { id: 'audience', label: 'Audience' },
  { id: 'send', label: 'Send' },
];

export function SurveyCompose() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id: routeId } = useParams<{ id?: string }>();

  const existing = useQuery({
    queryKey: ['survey', routeId],
    queryFn: () => getSurvey(routeId!),
    enabled: Boolean(routeId),
  });

  const [surveyId, setSurveyId] = useState<string | null>(routeId ?? null);
  const [draft, setDraft] = useState<SurveyDraftInput>(() => emptySurveyDraft());
  const [tab, setTab] = useState<Tab>('content');
  const [saveError, setSaveError] = useState<string | null>(null);
  const seeded = useRef(false);

  // Autosave snapshots (serialized so dirty checks compare like-for-like).
  const lastSavedSnapshotRef = useRef<string | null>(
    routeId ? null : JSON.stringify(emptySurveyDraft()),
  );
  const pendingSnapshotRef = useRef<string | null>(null);
  const errorSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    if (routeId && existing.data && !seeded.current) {
      const seededDraft = surveyToDraft(existing.data.survey);
      setDraft(seededDraft);
      lastSavedSnapshotRef.current = JSON.stringify(seededDraft);
      seeded.current = true;
    }
  }, [routeId, existing.data]);

  const update = (patch: Partial<SurveyDraftInput>) => setDraft((d) => ({ ...d, ...patch }));

  const draftJson = JSON.stringify(draft);
  const isDirty = lastSavedSnapshotRef.current !== draftJson;

  const save = useMutation({
    mutationFn: () => {
      pendingSnapshotRef.current = JSON.stringify(draft);
      return surveyId ? updateSurvey(surveyId, draft) : createSurvey(draft);
    },
    onSuccess: ({ survey }) => {
      setSaveError(null);
      errorSnapshotRef.current = null;
      lastSavedSnapshotRef.current = pendingSnapshotRef.current;
      qc.invalidateQueries({ queryKey: ['surveys'] });
      qc.setQueryData(['survey', survey.id], { survey, flowWarnings: [] });
      if (!surveyId) {
        setSurveyId(survey.id);
        seeded.current = true;
        navigate(`/surveys/${survey.id}/edit`, { replace: true });
      }
    },
    onError: (err) => {
      errorSnapshotRef.current = pendingSnapshotRef.current;
      setSaveError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const canSave = draft.title.trim().length > 0;
  const retryingFailedContent = errorSnapshotRef.current === draftJson;

  // Send (C5): flush any pending draft first (so the frozen content matches the
  // composer), then dispatch. On success the survey is sending/open → list.
  const send = useMutation({
    mutationFn: async () => {
      let id = surveyId;
      if (isDirty || !id) {
        const { survey } = await save.mutateAsync();
        id = survey.id;
      }
      if (!id) throw new Error('Save the survey before sending.');
      return sendSurvey(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surveys'] });
      navigate('/surveys');
    },
    onError: (err) => setSaveError(err instanceof ApiError ? err.message : String(err)),
  });

  useEffect(() => {
    if (
      !shouldAutosave({
        canSave,
        isDirty,
        saving: save.isPending,
        sending: send.isPending,
        confirming: false,
        retryingFailedContent,
      })
    ) {
      return;
    }
    const t = window.setTimeout(() => save.mutate(), AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftJson captures the draft; save is stable
  }, [draftJson, canSave, isDirty, save.isPending, send.isPending, retryingFailedContent]);

  const saveStatusLabel =
    SAVE_STATUS_LABEL[
      computeSaveStatus({ saving: save.isPending, isDirty, hasPersisted: Boolean(surveyId) })
    ];

  if (routeId && existing.isLoading) return <CenteredSpinner />;
  if (routeId && existing.error) {
    return <p className="text-sm text-red-600">Couldn't load this survey.</p>;
  }

  return (
    <div>
      <PageHeader
        title={surveyId ? 'Edit survey' : 'New survey'}
        backHref="/surveys"
        actions={
          <span className="text-sm text-slate-500" aria-live="polite">
            {saveStatusLabel}
          </span>
        }
      />

      {saveError && (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>
      )}

      {/* Survey title — internal label + the autosave gate. */}
      <div className="mb-6 max-w-xl">
        <label htmlFor="survey-title" className="mb-1 block text-sm font-medium text-slate-700">
          Survey title
        </label>
        <input
          id="survey-title"
          type="text"
          value={draft.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="EOFY participation — Fitstop"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
        <p className="mt-1 text-xs text-slate-400">Internal only — never shown to recipients.</p>
      </div>

      {/* Tab bar */}
      <div className="mb-6 border-b border-slate-200">
        <nav className="-mb-px flex gap-6" aria-label="Survey sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'border-b-2 px-1 py-3 text-sm font-medium transition-colors ' +
                (tab === t.id
                  ? 'border-kyck-600 text-kyck-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700')
              }
              aria-current={tab === t.id ? 'page' : undefined}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'content' && (
        <ContentTab
          intro={draft.introScreen}
          outro={draft.outroScreen}
          steps={draft.steps}
          linkMode={draft.linkMode}
          onIntroChange={(introScreen) => update({ introScreen })}
          onOutroChange={(outroScreen) => update({ outroScreen })}
          onStepsChange={(steps) => update({ steps })}
        />
      )}
      {tab === 'audience' && (
        <AudienceTab
          scope={draft.audienceScope}
          linkMode={draft.linkMode}
          onChange={(audienceScope) => update({ audienceScope })}
        />
      )}
      {tab === 'send' && (
        <SendTab
          draft={draft}
          onChange={(patch) => update(patch)}
          onLinkModeChange={(linkMode) =>
            // Apply the auto-name normalization client-side for instant preview;
            // the server re-applies it idempotently on PATCH (NEW-2).
            update({ linkMode, steps: normalizeSurveySteps(draft.steps, linkMode) })
          }
          sending={send.isPending}
          onSend={() => send.mutate()}
        />
      )}
    </div>
  );
}

// Content tab: intro editor (B2) → reorderable step list (B3) → outro editor
// (B2). Editing a step opens a per-type editor (B4 replaces the placeholder
// modal below); branching editing lands in B5.
function ContentTab({
  intro,
  outro,
  steps,
  linkMode,
  onIntroChange,
  onOutroChange,
  onStepsChange,
}: {
  intro: IntroScreen;
  outro: OutroScreen;
  steps: SurveyStep[];
  linkMode: SurveyLinkMode;
  onIntroChange: (next: IntroScreen) => void;
  onOutroChange: (next: OutroScreen) => void;
  onStepsChange: (next: SurveyStep[]) => void;
}) {
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const editingStep = steps.find((s) => s.id === editingStepId) ?? null;

  const replaceStep = (next: SurveyStep) =>
    onStepsChange(steps.map((s) => (s.id === next.id ? next : s)));

  return (
    <div className="space-y-4">
      <FlowIssuesBanner steps={steps} linkMode={linkMode} />
      <IntroEditor value={intro} onChange={onIntroChange} />
      <StepList
        steps={steps}
        linkMode={linkMode}
        onChange={onStepsChange}
        onEditStep={setEditingStepId}
      />
      <OutroEditor value={outro} onChange={onOutroChange} />

      {editingStep && (
        <StepEditorModal
          step={editingStep}
          allSteps={steps}
          linkMode={linkMode}
          onChange={replaceStep}
          onClose={() => setEditingStepId(null)}
        />
      )}
    </div>
  );
}

// Page-level live flow validation (B5): surfaces any validateSurveyFlow issue
// across the whole flow (e.g. a dangling branch target left by a deleted step),
// not just the per-rule errors inside the branching modal. Non-blocking — the
// draft still autosaves (NEW-1); this is guidance the AM resolves before send.
function FlowIssuesBanner({ steps, linkMode }: { steps: SurveyStep[]; linkMode: SurveyLinkMode }) {
  const issues = validateSurveyFlow(steps, linkMode);
  if (issues.length === 0) return null;
  const byStep = (id: string | null) =>
    id ? (steps.find((s) => s.id === id)?.kind === 'content' ? 'a content page' : 'a question') : 'the survey';
  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
      <p className="text-sm font-medium text-amber-800">
        {issues.length} {issues.length === 1 ? 'issue' : 'issues'} to resolve before sending
      </p>
      <ul className="mt-1 space-y-0.5 text-sm text-amber-700">
        {issues.slice(0, 6).map((e, i) => (
          <li key={`${e.code}-${i}`}>
            {e.message} <span className="text-amber-500">({byStep(e.stepId)})</span>
          </li>
        ))}
        {issues.length > 6 && <li className="text-amber-500">…and {issues.length - 6} more</li>}
      </ul>
    </div>
  );
}

